/**
 * V17.534 (RBAC 2A, brief 2026-10-29-002): stay signed in — the ONE access-token renewal path.
 *
 * `POST /users/refresh` reads the httpOnly refresh cookie (14 days since last use) and returns a
 * login-shaped body with a new access token. The token is written to `localStorage.access_token`
 * (the same key every client and the raw `fetch` pages read), so nothing else needs to change.
 *
 * Single flight:
 *  - within a tab, concurrent callers share one promise;
 *  - across tabs, `navigator.locks` (Chrome/Edge 69+, Firefox 96+, Safari 15.4+) serialises the
 *    call; without it, a `localStorage` lease (10 s) does, and a waiting tab takes the token the
 *    holder wrote (`storage` event). A tab that waited re-reads the token first and skips its own
 *    call when another tab already renewed it. The server's 30 s grace covers any race left.
 *
 * Never while offline (Phase 0c F01): an offline call returns `offline` and changes nothing.
 */
import axios, { type AxiosAdapter, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';
export const REFRESH_URL = `${API_BASE}/users/refresh`;
export const LOGOUT_URL = `${API_BASE}/users/logout`;
/** Fired on window after this tab stored a renewed token (other tabs see the `storage` event). */
export const TOKEN_REFRESHED_EVENT = 'vtw:token-refreshed';
const LOCK_NAME = 'vtw-session-refresh';
const LEASE_KEY = 'vtw_refresh_lease';
const LEASE_MS = 10_000;

export type RefreshResult =
  | { status: 'ok'; token: string }
  | { status: 'rejected' } // the session is over: 401 from /users/refresh
  | { status: 'offline' }
  | { status: 'error' }; // network / 5xx: keep the session, try again later

/** Seconds since epoch at which a JWT expires, or null. */
export function tokenExp(token: string | null): number | null {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/** True when the stored token is still good for at least `marginS` seconds. */
function storedTokenFresh(before: string | null, marginS = 60): string | null {
  const now = localStorage.getItem('access_token');
  if (!now || now === before) return null;
  const exp = tokenExp(now);
  return exp !== null && exp - Date.now() / 1000 > marginS ? now : null;
}

async function callRefresh(): Promise<RefreshResult> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return { status: 'offline' };
  let res: Response;
  try {
    res = await fetch(REFRESH_URL, { method: 'POST', credentials: 'include' });
  } catch {
    return { status: 'error' };
  }
  if (res.status === 401) return { status: 'rejected' };
  if (!res.ok) return { status: 'error' };
  try {
    const body = await res.json();
    if (!body?.access_token) return { status: 'error' };
    localStorage.setItem('access_token', body.access_token);
    if (body.user) localStorage.setItem('current_user', JSON.stringify(body.user));
    window.dispatchEvent(new CustomEvent(TOKEN_REFRESHED_EVENT, { detail: body }));
    return { status: 'ok', token: body.access_token };
  } catch {
    return { status: 'error' };
  }
}

/** Run `fn` while holding the cross-tab lease (fallback for browsers without navigator.locks). */
async function withLease<T>(fn: () => Promise<T>, before: string | null): Promise<T | { status: 'ok'; token: string }> {
  const me = Math.random().toString(36).slice(2);
  const readLease = (): { owner: string; until: number } | null => {
    try {
      return JSON.parse(localStorage.getItem(LEASE_KEY) || 'null');
    } catch {
      return null;
    }
  };
  const held = readLease();
  if (held && held.owner !== me && held.until > Date.now()) {
    // Another tab is renewing: wait for its token (storage event) or for the lease to lapse.
    const token = await new Promise<string | null>((resolve) => {
      const done = (t: string | null) => {
        window.removeEventListener('storage', onStorage);
        clearTimeout(timer);
        resolve(t);
      };
      const onStorage = (e: StorageEvent) => {
        if (e.key === 'access_token') done(storedTokenFresh(before));
      };
      window.addEventListener('storage', onStorage);
      const timer = setTimeout(() => done(storedTokenFresh(before)), Math.max(0, held.until - Date.now()));
    });
    if (token) return { status: 'ok', token };
  }
  try {
    localStorage.setItem(LEASE_KEY, JSON.stringify({ owner: me, until: Date.now() + LEASE_MS }));
  } catch {
    // storage blocked: run unguarded (the server grace still protects the rotation)
  }
  try {
    return await fn();
  } finally {
    try {
      if (readLease()?.owner === me) localStorage.removeItem(LEASE_KEY);
    } catch {
      // ignore
    }
  }
}

let inflight: Promise<RefreshResult> | null = null;

/** Renew the access token once, shared by every caller in this tab and serialised across tabs. */
export function refreshAccessToken(): Promise<RefreshResult> {
  if (inflight) return inflight;
  const before = localStorage.getItem('access_token');
  const run = async (): Promise<RefreshResult> => {
    const already = storedTokenFresh(before);
    if (already) return { status: 'ok', token: already }; // another tab renewed while we waited
    return callRefresh();
  };
  const locks = typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: LockManager }).locks : undefined;
  inflight = (locks?.request ? locks.request(LOCK_NAME, run) : withLease(run, before)) as Promise<RefreshResult>;
  inflight = inflight.finally(() => {
    inflight = null;
  });
  return inflight;
}

/** Best-effort server logout: revokes this login's refresh family and clears the cookie. */
export function serverLogout(): void {
  try {
    void fetch(LOGOUT_URL, { method: 'POST', credentials: 'include', keepalive: true }).catch(() => undefined);
  } catch {
    // never block a logout
  }
}

const AUTH_URL = /\/users\/(login|refresh|logout)(\?|$)/;

/**
 * Give an axios client the renewal: a 401 renews the access token once and re-sends the request
 * once with the new bearer; when the renewal is refused (or the request is an auth call), the 401
 * reaches the client's own interceptors exactly as before.
 *
 * Done in the ADAPTER, not a response interceptor: axios 1.13 discards a value returned from a
 * response-error interceptor (`.catch(onRejected).then(() => prevResult)`), so a retry there would
 * resolve to the request config instead of the response.
 */
export function installRefreshOn401(instance: AxiosInstance): void {
  const base = instance.defaults.adapter;
  const getAdapter = axios.getAdapter as unknown as (a: unknown, c?: unknown) => AxiosAdapter;
  instance.defaults.adapter = async (config: InternalAxiosRequestConfig) => {
    const adapter = getAdapter(base, config);
    try {
      return await adapter(config);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 401 || AUTH_URL.test(String(config.url ?? ''))) throw error;
      const result = await refreshAccessToken();
      if (result.status !== 'ok') throw error;
      config.headers.set('Authorization', `Bearer ${result.token}`);
      return adapter(config); // once: a second 401 goes to the client's interceptors
    }
  };
}
